import axios, { AxiosResponse } from 'axios'
import { handleError } from '@/api/ErrorHandlers'

export class ODataService implements IDataSource {
   constructor(private endpointUrl: string) {}

   public async fill(dataset: IDataSet): Promise<IDataSourceResult> {
      const baseUrl = this.endpointUrl
      const clauses = [
         this.generateQueryClause(dataset),
         this.generateExpandClause(dataset),
         this.generateOrderByClause(dataset),
         this.generatePagingClause(dataset),
         this.generateFilterClause(dataset),
      ]

      const queryString = clauses.filter(c => c.length > 0).join('&')

      try {
         const url = `/odata/${baseUrl}?${queryString}`

         const response: AxiosResponse = await axios.get(url)

         if (response.status === 200) {
            return {
               items: response.data.value,
               count: +response.data['@odata.count'],
            }
         } else {
            return { items: [], count: 0 }
         }
      } catch (err) {
         handleError(err.response)
         return { items: [], count: 0 }
      }
   }

   private generateQueryClause(dataset: IDataSet) {
      return dataset.query
         .map(q => `${q.name}=${encodeURIComponent(q.value ? q.value.toString() : '')}`)
         .join('&')
   }

   private generateExpandClause(dataset: IDataSet) {
      if (dataset.expand.length === 0) {
         return ''
      }
      return `$expand=${dataset.expand.join(',')}`
   }

   private generateOrderByClause(dataset: IDataSet) {
      return `$orderby=${dataset.sortColumn}%20${dataset.sortDirection}`
   }

   private generatePagingClause(dataset: IDataSet) {
      return `$count=true&$top=${dataset.pageSize}&$skip=${dataset.firstItemIndex}`
   }

   private generateFilterClause(dataset: IDataSet) {
      if (dataset.filters.length === 0) {
         return ''
      }
      const filterClauses = dataset.filters
         .map(filter => this.generateFilterStatement(filter))
         .filter(f => !!f)

      return filterClauses.length > 0 ? '$filter=' + filterClauses.join('%20and%20') : ''
   }

   private generateFilterStatement(filter: ODataFilter) {
      if (!filter.value) {
         return ''
      }

      const filterValue = this.generateFilterValue(filter)

      if (filter.comparison === 'substring') {
         return `contains(${filter.name},%27${filterValue}%27)`
      }

      if (filter.comparison === 'any' && filter.type === 'guidlist') {
         return this.generateGuidListFilter(filter)
      }

      if (filter.comparison === 'any' && filter.expanded) {
         const key = filter.key ? filter.key : 'id'
         return `${filter.name}/any(${filter.name}:${filter.name}/${key}%20eq%20${filterValue})`
      }

      if (filter.comparison === 'any') {
         return `${filter.name}/any(id:%20id%20eq%20${filterValue})`
      }

      return `${filter.name}%20${filter.comparison}%20${filterValue}`
   }

   private generateFilterValue(filter: ODataFilter) {
      if (filter.type === 'guid') {
         return `${filter.value}`
      }
      if (filter.type === 'datetime') {
         return `${filter.value}`
      }
      if (typeof filter.value === 'string' && filter.value.includes("'")) {
         return filter.value.replace(/'/g, "''")
      }
      return filter.value
   }

   private generateGuidListFilter(filter: ODataFilter) {
      const values = filter.value as string[]
      if (values.length === 0) {
         return ''
      }
      //console.log(filter.name, filter.value, ' values='+values.toString())
      const valueClauses = values.map(guid => `id%20eq%20${guid}`)
      return `${filter.name}/any(id:%20${valueClauses.join('%20or%20')})`
   }
}
